import React, { useState, useEffect } from 'react';
import {
  Building2, Plus, Users, FileText, CheckCircle2, Clock,
  Search, Shield, Copy, Check, ExternalLink, RefreshCw,
  Sliders, AlertCircle, Eye, EyeOff, Lock, Globe,
  Briefcase, Scale, Calculator, UserCheck, FileCheck, Key,
  Sparkles, Bot, Phone, Mail, MapPin, CheckSquare
} from 'lucide-react';

interface CorporateMasterPanelProps {
  authHeader: () => Record<string, string>;
}

const ALL_SERVICES = [
  { id: 'tax_filling', label: 'Business Tax & Return Filing', icon: Calculator, color: 'text-emerald-400' },
  { id: 'secretarial', label: 'Company Secretarial Services', icon: Briefcase, color: 'text-cyan-400' },
  { id: 'consulting', label: 'Business Consulting & Strategy', icon: Globe, color: 'text-purple-400' },
  { id: 'hrm', label: 'HRM & Payroll Services', icon: Users, color: 'text-pink-400' },
  { id: 'accounting', label: 'Accounting & Financials', icon: FileText, color: 'text-amber-400' },
  { id: 'auditing_internal', label: 'Internal Auditing', icon: Shield, color: 'text-indigo-400' },
  { id: 'auditing_external', label: 'External Auditing', icon: FileCheck, color: 'text-blue-400' },
  { id: 'legal', label: 'Legal & Contract Services', icon: Scale, color: 'text-rose-400' },
  { id: 'other', label: 'Other Corporate Advisory', icon: Sparkles, color: 'text-teal-400' },
];

export default function CorporateMasterPanel({ authHeader }: CorporateMasterPanelProps) {
  const [firms, setFirms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);

  // New Firm Form State
  const [newFirm, setNewFirm] = useState({
    name: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    phone: '',
    address: '',
    servicesEnabled: ALL_SERVICES.map(s => s.id),
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const fetchFirms = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/corporate/admin/firms', { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setFirms(data.firms || []);
      }
    } catch (err: any) {
      console.error('Error loading corporate firms:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFirms();
  }, []);

  const handleCreateFirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const res = await fetch('/api/corporate/admin/firms', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(newFirm)
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg(`Firm "${data.firm.name}" provisioned successfully!`);
        setIsCreateModalOpen(false);
        setNewFirm({
          name: '',
          adminName: '',
          adminEmail: '',
          adminPassword: '',
          phone: '',
          address: '',
          servicesEnabled: ALL_SERVICES.map(s => s.id),
        });
        fetchFirms();
      } else {
        setError(data.error || 'Failed to create firm');
      }
    } catch (err: any) {
      setError(err?.message || 'Network error');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (firmId: number, currentStatus: string) => {
    const nextStatus = currentStatus === 'active' ? 'suspended' : 'active';
    setStatusUpdatingId(firmId);
    try {
      const res = await fetch(`/api/corporate/admin/firms/${firmId}/status`, {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify({ status: nextStatus })
      });
      const data = await res.json();
      if (data.success) {
        setFirms(firms.map(f => f.id === firmId ? { ...f, status: nextStatus } : f));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const filteredFirms = firms.filter(f => 
    f.name?.toLowerCase().includes(search.toLowerCase()) ||
    f.adminEmail?.toLowerCase().includes(search.toLowerCase()) ||
    f.adminName?.toLowerCase().includes(search.toLowerCase())
  );

  const totalClients = firms.reduce((acc, f) => acc + (f.totalClients || 0), 0);
  const totalFilings = firms.reduce((acc, f) => acc + (f.totalFilings || 0), 0);
  const pendingFilings = firms.reduce((acc, f) => acc + (f.pendingFilings || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header & Stats Banner */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-gradient-to-r from-blue-950/60 via-indigo-950/40 to-slate-900/60 border border-blue-500/30 p-6 rounded-3xl backdrop-blur-md shadow-2xl">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black text-white tracking-wide flex items-center gap-2">
                KANGAVENA CORPORATE SUITE
                <span className="text-[10px] font-black bg-blue-500/20 text-blue-300 border border-blue-500/40 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                  MASTER CONTROL
                </span>
              </h2>
              <p className="text-xs text-slate-400">Manage Advisory Firms, Company Secretarial Portals, Tax & Legal Entities</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchFirms}
            disabled={loading}
            className="p-3 bg-slate-900/80 hover:bg-slate-800 text-slate-300 rounded-xl border border-white/10 transition"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => { setError(null); setIsCreateModalOpen(true); }}
            className="px-5 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-blue-500/25 flex items-center gap-2 transition"
          >
            <Plus className="w-4 h-4" />
            <span>Provision New Firm</span>
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-4">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Firms</span>
          <p className="text-2xl font-black text-white mt-1">{firms.length}</p>
        </div>
        <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-4">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Active Clients</span>
          <p className="text-2xl font-black text-cyan-400 mt-1">{totalClients}</p>
        </div>
        <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-4">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Filings</span>
          <p className="text-2xl font-black text-emerald-400 mt-1">{totalFilings}</p>
        </div>
        <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-4">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Pending Reviews</span>
          <p className="text-2xl font-black text-amber-400 mt-1">{pendingFilings}</p>
        </div>
      </div>

      {successMsg && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs px-4 py-3 rounded-xl flex items-center justify-between">
          <span className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            {successMsg}
          </span>
          <button onClick={() => setSuccessMsg(null)} className="text-emerald-400 hover:text-white text-xs">Dismiss</button>
        </div>
      )}

      {/* Search & Firms Grid */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-4 top-3.5" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search corporate advisory firms by name, email, or admin..."
            className="w-full bg-slate-950/80 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
            <p className="text-xs">Loading Corporate Entities...</p>
          </div>
        ) : filteredFirms.length === 0 ? (
          <div className="text-center py-16 bg-slate-900/30 border border-white/5 rounded-3xl">
            <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <h3 className="text-sm font-bold text-slate-400">No Corporate Firms Found</h3>
            <p className="text-xs text-slate-500 mt-1">Click "Provision New Firm" to onboard an advisory firm.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredFirms.map((firm) => (
              <div
                key={firm.id}
                className="bg-slate-900/60 border border-white/10 hover:border-blue-500/40 rounded-2xl p-5 backdrop-blur-md transition-all shadow-lg relative group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 font-black text-base">
                      {firm.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="text-base font-black text-white">{firm.name}</h4>
                      <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5">
                        <Mail className="w-3 h-3 text-slate-500" />
                        {firm.adminEmail}
                      </p>
                    </div>
                  </div>

                  <span className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border ${
                    firm.status === 'active' 
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                      : 'bg-rose-500/10 text-rose-400 border-rose-500/30'
                  }`}>
                    {firm.status}
                  </span>
                </div>

                {/* Info & Stats Chips */}
                <div className="grid grid-cols-3 gap-2 mt-4 bg-slate-950/60 p-2.5 rounded-xl border border-white/5 text-center">
                  <div>
                    <span className="text-[10px] text-slate-500 uppercase font-bold block">Clients</span>
                    <span className="text-xs font-black text-cyan-300">{firm.totalClients || 0}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 uppercase font-bold block">Filings</span>
                    <span className="text-xs font-black text-emerald-300">{firm.totalFilings || 0}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 uppercase font-bold block">Pending</span>
                    <span className="text-xs font-black text-amber-300">{firm.pendingFilings || 0}</span>
                  </div>
                </div>

                {/* API Key Box */}
                <div className="mt-3 flex items-center justify-between bg-black/40 px-3 py-2 rounded-lg border border-white/5 text-[11px]">
                  <span className="text-slate-500 font-mono truncate max-w-[200px]">
                    API Key: {firm.apiKey}
                  </span>
                  <button
                    onClick={() => copyToClipboard(firm.apiKey, `key-${firm.id}`)}
                    className="text-slate-400 hover:text-white flex items-center gap-1 transition"
                  >
                    {copiedKey === `key-${firm.id}` ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedKey === `key-${firm.id}` ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 pt-3 border-t border-white/5 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[11px] text-slate-500">
                    Admin: <strong className="text-slate-300">{firm.adminName}</strong>
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => copyToClipboard(`${window.location.origin}/corporate`, `portal-${firm.id}`)}
                      className="text-xs font-bold px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition flex items-center gap-1"
                      title="Copy Client Portal URL"
                    >
                      <Globe className="w-3.5 h-3.5" />
                      <span>{copiedKey === `portal-${firm.id}` ? 'Copied' : 'Client Link'}</span>
                    </button>
                    <a
                      href="/corporate/admin"
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-bold px-2.5 py-1.5 rounded-lg bg-blue-600/20 text-blue-300 border border-blue-500/30 hover:bg-blue-600/30 transition flex items-center gap-1"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span>Firm Portal</span>
                    </a>
                    <button
                      onClick={() => handleToggleStatus(firm.id, firm.status)}
                      disabled={statusUpdatingId === firm.id}
                      className={`text-xs font-bold px-2.5 py-1.5 rounded-lg border transition ${
                        firm.status === 'active'
                          ? 'border-rose-500/30 text-rose-400 hover:bg-rose-500/10'
                          : 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
                      }`}
                    >
                      {firm.status === 'active' ? 'Suspend' : 'Activate'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Provision New Firm Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-white">Provision Corporate Advisory Firm</h3>
                  <p className="text-xs text-slate-400">Create a firm workspace with dedicated admin credentials</p>
                </div>
              </div>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-500 hover:text-white text-sm">✕</button>
            </div>

            {error && (
              <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs px-4 py-3 rounded-xl mb-4 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleCreateFirm} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1">Firm / Company Name *</label>
                <input
                  type="text"
                  required
                  value={newFirm.name}
                  onChange={(e) => setNewFirm({ ...newFirm, name: e.target.value })}
                  placeholder="e.g., Apex Corporate Advisory Ltd"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Managing Director / Admin Name *</label>
                  <input
                    type="text"
                    required
                    value={newFirm.adminName}
                    onChange={(e) => setNewFirm({ ...newFirm, adminName: e.target.value })}
                    placeholder="e.g., John Perera"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Admin Email Address *</label>
                  <input
                    type="email"
                    required
                    value={newFirm.adminEmail}
                    onChange={(e) => setNewFirm({ ...newFirm, adminEmail: e.target.value })}
                    placeholder="admin@firm.com"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Admin Password *</label>
                  <input
                    type="password"
                    required
                    value={newFirm.adminPassword}
                    onChange={(e) => setNewFirm({ ...newFirm, adminPassword: e.target.value })}
                    placeholder="••••••••"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1">Phone Number</label>
                  <input
                    type="text"
                    value={newFirm.phone}
                    onChange={(e) => setNewFirm({ ...newFirm, phone: e.target.value })}
                    placeholder="+94 77 123 4567"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1">Physical Office Address</label>
                <input
                  type="text"
                  value={newFirm.address}
                  onChange={(e) => setNewFirm({ ...newFirm, address: e.target.value })}
                  placeholder="e.g., Level 12, World Trade Center, Colombo"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 mb-2">Enabled Corporate Modules</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-slate-950 p-3 rounded-xl border border-slate-800">
                  {ALL_SERVICES.map((srv) => {
                    const isChecked = newFirm.servicesEnabled.includes(srv.id);
                    return (
                      <label
                        key={srv.id}
                        className="flex items-center gap-2 text-xs text-slate-300 hover:text-white cursor-pointer select-none p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewFirm({ ...newFirm, servicesEnabled: [...newFirm.servicesEnabled, srv.id] });
                            } else {
                              setNewFirm({ ...newFirm, servicesEnabled: newFirm.servicesEnabled.filter(id => id !== srv.id) });
                            }
                          }}
                          className="rounded border-slate-700 text-blue-600 focus:ring-0"
                        />
                        <srv.icon className={`w-3.5 h-3.5 ${srv.color}`} />
                        <span>{srv.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2.5 text-xs font-bold text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-blue-500/25 flex items-center gap-2 transition"
                >
                  {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  <span>Provision Firm</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
